import axios from "axios";
import { Machine, spawn } from "xstate";
import { assign, log, pure } from "xstate/lib/actions";
import moment from "moment";
import paramsMachine from "./params-machine";
import rowWindowMachine from "./row-window-machine";
import formats from "./date-formats";

const numberFormat = Intl.NumberFormat("en-US").format;

// An XState state machine to sequence our report data loading (https://xstate.js.org/docs/)

const reportMachine = Machine(
    {
        id: "measurementReport",
        initial: "params",
        context: {
            windowColumns: 50,
            windowRows: 50
        },
        states: {
            // First we handle the report "params", which includes
            // loading the selected podcast and the bounds for the
            // selected report. For any params that aren't provided
            // we figure out defaults. This invokes a child machine
            // which will terminate when it's done and return the
            // loaded metadata.
            params: {
                invoke: {
                    src: paramsMachine,
                    data: ({
                        accountID,
                        podcastID,
                        podcast,
                        report,
                        period,
                        bounds,
                        canned,
                        reportStart,
                        reportEnd
                    }) => ({
                        accountID,
                        podcastID,
                        podcast,
                        report,
                        period,
                        bounds,
                        canned,
                        reportStart,
                        reportEnd
                    }),
                    onDone: {
                        target: "matrix",
                        actions: ["updateParams", "syncParams"]
                    }
                }
            },

            // Once we've sorted out the report params we load the
            // "matrix" which tells us how many rows and columns
            // we have, and how to divide the data into chunks (aka "windows")
            matrix: {
                initial: "loading",
                states: {
                    loading: {
                        invoke: {
                            src: "getMatrix",
                            onDone: {
                                target: "#measurementReport.run",
                                actions: "setMatrix"
                            },
                            onError: "error"
                        }
                    },
                    error: {}
                }
            },

            // Then we enter a parallel state where we simultaneously load
            // all of the column totals for the report, as well as begin
            // to load the grid data.
            run: {
                type: "parallel",
                states: {
                    columnTotals: {
                        initial: "loading",
                        states: {
                            loading: {
                                invoke: {
                                    src: "getColumnTotals",
                                    onDone: {
                                        target: "ready",
                                        actions: "setColumnTotals"
                                    },
                                    onError: "error"
                                }
                            },
                            ready: {},
                            error: {}
                        }
                    },
                    gridData: {
                        initial: "first",
                        // We "know" that first viewPort is going to be the upper-right corner
                        // (and it takes a few renders before the grid position stablizes)
                        // so we start getting the first window of data before we actually listen
                        // to the viewPort position
                        states: {
                            first: {
                                entry: "scheduleGridData",
                                always: "any"
                            },
                            any: {
                                // we just idle here and wait for any changes to the viewport
                                on: {
                                    VIEWPORT: {
                                        actions: [/*"logViewport",*/ "scheduleGridData"]
                                    }
                                }
                            }
                        }
                    }
                }
            }
        },
        on: {
            // An external event used to change the report params, which causes us to
            // re-invoke the params machine (but only if any params have changed).
            PARAMS: { target: "params", actions: "resetChangedParams", cond: "paramsChanged" },

            // An internal event that is raised when a child machine has loaded row totals
            ROW_TOTALS: { actions: "receiveRowTotals" },

            // An internal event that is raised when a child machine has loaded cell data
            CELLS: { actions: [log((context, event) => event), "receiveCells"] }
        }
    },
    {
        actions: {
            scheduleGridData: assign({
                rowMachines: (
                    {
                        rowMachines = {},
                        accountID,
                        podcastID,
                        report,
                        period,
                        reportStart,
                        reportEnd,
                        windowColumns,
                        windowRows,
                        feedFilter,
                        columnCount,
                        columnWindows
                    },
                    { viewPort }
                ) => {
                    const viewRowWindows = new Set();
                    if (!viewPort) {
                        // This is called once from the "first" state, and since that isn't
                        // entered from a VEIWPORT event the viewPort is undefined, which
                        // tells us that we want to load the first row window.
                        viewRowWindows.add(0);
                    } else {
                        // Otherwise we figure out which row windows overlap the current viewPort.
                        for (const r of [viewPort.rowStart, viewPort.rowEnd]) {
                            const rowWindow = Math.floor(r / windowRows);
                            if (rowWindow >= 0) viewRowWindows.add(rowWindow);
                        }
                    }

                    // Then we figure out which column windows overlap the current viewPort
                    // (and when there's no viewPort in the "first" state we just assume
                    // that the right-most column is in view).
                    const viewColumnWindows = new Set();
                    for (const c of viewPort
                        ? [viewPort.colEnd, viewPort.colStart]
                        : [columnCount - 1]) {
                        const columnIndex = Math.floor(c / windowColumns);
                        if (columnIndex >= 0) {
                            viewColumnWindows.add(columnWindows[columnIndex]);
                        }
                    }

                    // For each row window we spawn a child machine that will
                    // handle all of the data for that window.
                    for (const rowWindow of viewRowWindows) {
                        let machine = rowMachines[rowWindow];
                        if (!machine) {
                            machine = spawn(
                                rowWindowMachine.withContext({
                                    accountID,
                                    podcastID,
                                    report,
                                    period,
                                    reportStart,
                                    reportEnd,
                                    windowColumns,
                                    windowRows,
                                    feedFilter,
                                    rowWindow
                                }),
                                `rowWindow-${rowWindow}`
                            );
                            rowMachines[rowWindow] = machine;
                        }
                        // And we send an event to the row machine telling
                        // it which sets of cells to load.
                        for (const columnWindow of viewColumnWindows) {
                            machine.send("COLUMN_WINDOW", { columnWindow });
                        }
                    }

                    return { ...rowMachines };
                }
            }),

            // various actions that set or reset the context on state transitions
            clearDates: assign({ reportStart: undefined, reportEnd: undefined }),
            clearBounds: assign({ bounds: undefined, canned: undefined }),
            clearData: assign({
                matrix: undefined,
                columnWindows: undefined,
                columns: undefined,
                columnsFormatted: undefined,
                rowCount: undefined,
                columnCount: undefined,
                columnTotals: undefined,
                columnTotalsFormatted: undefined,
                reportTotal: undefined,
                rowTotals: undefined,
                viewPort: undefined,
                rowMachines: ({ rowMachines = {} }) => {
                    for (const rowWindow in rowMachines) {
                        rowMachines[rowWindow].stop();
                    }
                    return undefined;
                },
                cells: undefined
            }),
            logViewport: log((context, { viewPort }) => viewPort, "viewPort"),
            resetPodcast: assign({
                podcastID: (context, event) => event.podcastID,
                podcast: undefined,
                feedFilter: undefined
            }),
            resetReport: assign({ report: (context, event) => event.report }),
            resetPeriod: assign({ period: (context, event) => event.period }),
            resetDates: assign({
                reportStart: (context, event) => event.reportStart,
                reportEnd: (context, event) => event.reportEnd
            }),
            resetChangedParams: pure((context, event) => {
                // This could probably be modelled differently, but
                // we just figure out which params have actually changed
                // and return the correct actions for updating the context.
                // This happens here and not in the paramsMachine because
                // we want the context to change immediatelly so the ui
                // doesn't leave any stale data lingering. (Probably cleaner
                // if the paramsMachine sent us an event or something?)
                const actions = new Set();
                if (context.podcastID != event.podcastID) {
                    actions.add("resetPodcast");
                    actions.add("clearBounds");
                }
                if (context.report != event.report) {
                    actions.add("resetReport");
                    actions.add("clearBounds");
                }
                if (context.period != event.period) {
                    actions.add("resetPeriod");
                    actions.add("clearDates");
                }
                if (
                    context.reportStart != event.reportStart ||
                    context.reportEnd != event.reportEnd
                ) {
                    actions.add("resetDates");
                }
                actions.add("clearData");
                return [...actions];
            }),
            setMatrix: assign({
                matrix: (context, event) => event.data,
                columns: (context, event) => event.data.columns,
                rowCount: (context, event) => event.data.totalRows,
                columnCount: (context, event) => event.data.totalColumns,
                columns: (context, event) => event.data.columns,
                columnWindows: (context, event) => event.data.columnWindows,
                columnsFormatted: ({ period }, { data: { columns } }) =>
                    columns.map(c => moment(c).format(formats[period].short))
            }),

            // Re-shape the data from the API calls into something more useful. This
            // receives an event with an array of result data, one result from each
            // of the separate column total API calls. We flatten that into single objects
            // for the column totals keyed by column date, as well as summing up the
            // grand total for the report.
            setColumnTotals: assign({
                columnTotals: (context, event) =>
                    event.data.reduce((acc, { columnTotals }) => ({ ...acc, ...columnTotals }), {}),
                columnTotalsFormatted: (context, event) =>
                    event.data.reduce((acc, { columnTotals }) => {
                        for (const key in columnTotals) {
                            acc[key] = numberFormat(columnTotals[key]);
                        }
                        return acc;
                    }, {}),
                reportTotal: (context, event) =>
                    event.data.reduce((acc, { subTotal }) => acc + subTotal, 0)
            }),

            // The row totals is an object keyed by row index. Each row window
            // recieved from a child machine gets merged into the current context.
            receiveRowTotals: assign({
                rowTotals: ({ rowTotals: current = {} }, { rowTotals }) => ({
                    ...current,
                    ...rowTotals
                })
            }),

            // Here we recieve a chunk of cell data from one of the child row machines.
            // The cells object contains each individual cell keyed by its absolute row
            // and column in the overall grid. Below is some monkey business to figure
            // out the absolute column for the given column "window" by calculating
            // the correct index offsets, and then merging that into the current context.
            receiveCells: assign({
                cells: (
                    { cells: current = {}, columnWindows, columns, windowColumns },
                    { columnWindow, cells }
                ) => {
                    const columnWindowIndex = columnWindows.indexOf(columnWindow),
                        start = columnWindowIndex * windowColumns,
                        cellColumns = columns.slice(start, start + windowColumns);

                    return Object.keys(cells).reduce(
                        (acc, key) => ({
                            ...acc,
                            [key]: cellColumns.reduce(
                                (cacc, column, index) => ({
                                    ...cacc,
                                    [index + start]:
                                        cells[key] && cells[key][column] >= 0
                                            ? numberFormat(cells[key][column])
                                            : ""
                                }),
                                current[key] || {}
                            )
                        }),
                        current
                    );
                }
            }),
            setViewport: assign({
                viewPort: (context, { colStart, rowStart, colEnd, rowEnd }) => ({
                    colStart,
                    rowStart,
                    colEnd,
                    rowEnd
                })
            }),
            updateParams: assign((context, { data }) => data),

            // not all use cases for the report state will want to have the report
            // params on the browser url, so we just leave a hook that the client
            // can override if it wants to (which we will for the main reports)
            syncParams: log((_, { data }) => data, "syncParams")
        },
        guards: {
            paramsChanged: (context, event) =>
                context.podcastID != event.podcastID ||
                context.report != event.report ||
                context.period != event.period ||
                context.reportStart != event.reportStart ||
                context.reportEnd != event.reportEnd
        },
        services: {
            getColumnTotals: (
                {
                    accountID,
                    podcastID,
                    report,
                    period,
                    windowColumns,
                    windowRows,
                    feedFilter,
                    matrix
                },
                event
            ) =>
                Promise.all(
                    // fire off a request for every column window in the report and
                    // wait for them all to finish
                    matrix.columnWindows.map(range =>
                        axios({
                            url: `/api/${accountID}/measurement/reports/${podcastID}/${report}/${period}/column-totals/${range}`,
                            params: { windowColumns, windowRows, feedFilter }
                        }).then(({ data }) => data)
                    )
                ),
            getMatrix: (
                {
                    accountID,
                    podcastID,
                    report,
                    period,
                    reportStart,
                    reportEnd,
                    windowColumns,
                    windowRows,
                    feedFilter
                },
                event
            ) =>
                axios({
                    url: `/api/${accountID}/measurement/reports/${podcastID}/${report}/${period}/matrix/${reportStart}/${reportEnd}`,
                    params: { windowColumns, windowRows, feedFilter }
                }).then(({ data: matrix }) => matrix)
        }
    }
);

export default reportMachine;
